import { Suspense, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { RotateCw } from 'lucide-react'
import { Outlet, useLocation } from 'react-router-dom'
import { useShallow } from 'zustand/react/shallow'
import { Moment, MOMENT_SLUGS, PORTAL_PAGES } from '@/constants'
import { useSessionStore, useUIStore } from '@/stores'
import { useIsCompact } from '@hooks/useDevice'
import { useToast } from '@/hooks/useToast'
import { ToastContainer } from '@/components/ui/ToastContainer'
import Alert from '@/components/Alert'
import { GlobalLoading } from '@components/LoadingSpinner'
import { formatCurrency, formatErrorMessage } from '@utils'
import type { PortalSection, PortalSettings } from '@/types'
import OmpHeader from './sections/OmpHeader'
import OmpAnnouncementBar from './sections/OmpAnnouncementBar'
import OmpWelcomeMessage from './sections/OmpWelcomeMessage'
import OmpNavigationalBlocks from './sections/OmpNavigationalBlocks'
import OmpFooter from './sections/OmpFooter'
import OmpPaymentIssueBanner from './components/OmpPaymentIssueBanner'
import OmpPaymentUpdateAutoOpen from './components/OmpPaymentUpdateAutoOpen'
import { OmpActionsProvider } from './OmpActionContext'
import OmpActionRouter from './drawers/OmpActionRouter'
import OmpLifeline from './OmpLifeline'
import OmpCrossSellCartHost from './components/OmpCrossSellCartHost'
import { applyOmpTheme, getThemeVariables } from './utils/themeTokens'
import {
  isFooterVisible,
  resolveAnnouncement,
  resolveNavLabels,
  resolveSectionMeta,
  resolveWelcome,
} from './utils/sectionsConfig'
import {
  hasVisibleHtml,
  interpolateTemplate,
  readHtmlAlignment,
  resolveProductImage,
  sessionTokens,
  stripHtmlTextColor,
} from './utils/omp'
import { isDrawerOwningFunnel } from './utils/portalSession'
import { isSyncedToUrl, moveSessionToPath } from './utils/portalRecovery'
import { pageFromPath } from './utils/portalNav'
import { isPortalNavPending, queuePortalNav } from './utils/portalNavQueue'
import { trackStepSnapshots } from './utils/stepSnapshots'
import { usePortalNavPending } from './hooks/usePortalNavPending'
import { usePortalStep } from './hooks/usePortalStep'
import { zoneFor } from './utils/sectionZones'
import PreviewSlot from './PreviewSlot'
import { isInteractivePreviewMode } from '@/utils/previewMode'
import OmpRouteSkeleton from './components/OmpRouteSkeleton'
import { preloadOmpPages } from './pageLoaders'
import { SectionKey } from '@/constants'
import { preloadImage } from '@/utils/imageCache'
import { isPreviewSession } from '@utils/session'
import { environment } from '@/constants/environment'

function showNavError(err: unknown): void {
  if (!useSessionStore.getState().session) {
    return
  }
  useUIStore.getState().addUIMessage({ type: 'error', message: formatErrorMessage(err) })
}

const OmpLayout = () => {
  const location = useLocation()
  const toast = useToast()
  const campaign = useSessionStore(s => s.campaign) as any
  const credits = useSessionStore(s => (s.session as any)?.customer?.store_credit)
  const page = pageFromPath(location.pathname)
  const { moment } = usePortalStep()
  const navPending = usePortalNavPending()
  const contentRef = useCallback(
    (content: HTMLElement | null) => {
      if (navPending) {
        content?.setAttribute('inert', '')
      } else {
        content?.removeAttribute('inert')
      }
    },
    [navPending]
  )
  const momentImage = useSessionStore(s => {
    const item = (s.session as any)?.moment_item
    return item ? resolveProductImage(item) : undefined
  })
  const welcomeTokens = useSessionStore(useShallow(s => sessionTokens(s.session)))
  const isPreview = useSessionStore(s => isPreviewSession(s.session))

  // Disabled temporarily until new banner section is implemented.
  const isPaymentIssueBannerDisabled = true

  const [refreshing, setRefreshing] = useState(false)
  const refreshSettings = async () => {
    setRefreshing(true)
    try {
      await useSessionStore.getState().getSession()
      toast.success('Settings refreshed')
    } catch {
      toast.error('Could not refresh settings')
    } finally {
      setRefreshing(false)
    }
  }

  const portalSettings = campaign?.portal_settings as PortalSettings | undefined
  const themeVariables = getThemeVariables(campaign)

  useEffect(() => {
    applyOmpTheme(themeVariables)
  }, [themeVariables])

  useEffect(() => {
    if (typeof window.requestIdleCallback === 'function') {
      const id = window.requestIdleCallback(preloadOmpPages)
      return () => window.cancelIdleCallback(id)
    }
    const id = window.setTimeout(preloadOmpPages, 1500)
    return () => window.clearTimeout(id)
  }, [])

  useEffect(() => {
    const onPop = () => {
      if (isInteractivePreviewMode()) {
        return
      }
      const path = window.location.pathname + window.location.search
      if (!isPortalNavPending() && (isDrawerOwningFunnel() || isSyncedToUrl(path))) {
        return
      }
      queuePortalNav(async isLatest => {
        if (isDrawerOwningFunnel()) {
          return
        }
        try {
          await moveSessionToPath(path, isLatest)
        } catch (err) {
          if (isLatest()) {
            showNavError(err)
          }
        }
      }).catch(showNavError)
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [])

  useEffect(() => trackStepSnapshots(), [])

  useEffect(() => {
    if (momentImage) {
      preloadImage(momentImage)
    }
  }, [momentImage])
  const navLabels = useMemo(() => resolveNavLabels(portalSettings, page, moment), [portalSettings, page, moment])
  const announcement = useMemo(() => resolveAnnouncement(portalSettings, page, moment), [portalSettings, page, moment])
  const welcome = useMemo(() => resolveWelcome(portalSettings, page, moment), [portalSettings, page, moment])
  const showFooter = useMemo(() => isFooterVisible(portalSettings, page, moment), [portalSettings, page, moment])
  const navMeta = useMemo(
    () => resolveSectionMeta(portalSettings, SectionKey.NavigationalBlocks, page, moment),
    [portalSettings, page, moment]
  )
  const welcomeMeta = useMemo(
    () => resolveSectionMeta(portalSettings, SectionKey.WelcomeMessage, page, moment),
    [portalSettings, page, moment]
  )

  const isCompact = useIsCompact()
  const welcomeData = welcome.data
  const welcomeHeading = useMemo(
    () => (welcome.visible ? interpolateTemplate(welcomeData.heading, welcomeTokens) : ''),
    [welcome.visible, welcomeData, welcomeTokens]
  )
  const requested = themeVariables?.navigation_blocks_position
  const navPosition = requested === 'right' || requested === 'top' ? requested : 'left'
  const isTop = navPosition === 'top'
  const welcomeAlign = readHtmlAlignment(welcomeData.heading)
  // /portal/account maps to the home page's config, but the welcome message is Home-only.
  const isHomePath = location.pathname === '/portal' || location.pathname === '/portal/'
  const showWelcome = isHomePath && hasVisibleHtml(welcomeHeading)
  const showCredits = typeof credits === 'number' && credits > 0

  // Mobile/tablet put the welcome row and the nav inside the header (Figma 6618:71669),
  // so both surfaces render the same markup from one place.
  // CHROME_KEYS are skipped by OmpSectionRenderer, so the overlay is injected here.
  const framed = (key: SectionKey, meta: PortalSection | undefined, node: ReactNode) => (
    <PreviewSlot
      uid={meta?.uid}
      sectionKey={key}
      index={0}
      zone={zoneFor(key)}
      label={meta?.name}
      deletable={meta?.deletable}>
      {node}
    </PreviewSlot>
  )

  // Top nav and mobile/tablet put the greeting inside the nav, which owns its colour.
  const greetingInNav = isTop || isCompact
  const greetingData = useMemo(
    () => (greetingInNav ? { ...welcomeData, heading: stripHtmlTextColor(welcomeData.heading) } : welcomeData),
    [greetingInNav, welcomeData]
  )
  const welcomeNode = showWelcome
    ? framed(SectionKey.WelcomeMessage, welcomeMeta, <OmpWelcomeMessage data={greetingData} />)
    : null
  const creditsNode = showCredits ? <span className="omp-credits">Credits: {formatCurrency(credits)}</span> : null
  const welcomeRow =
    welcomeNode || creditsNode ? (
      <div className="omp-welcome-row" data-welcome-align={welcomeAlign}>
        {welcomeNode}
        {creditsNode}
      </div>
    ) : null
  // Top nav and mobile/tablet move the greeting into the nav, leaving credits on their own row.
  const creditsRow = creditsNode ? <div className="omp-welcome-row omp-credits-row">{creditsNode}</div> : null

  const navBlocks = (layout?: 'top', greeting?: ReactNode) =>
    framed(
      SectionKey.NavigationalBlocks,
      navMeta,
      <OmpNavigationalBlocks labels={navLabels} layout={layout} greeting={greeting} />
    )

  const content = (
    <section ref={contentRef} className="omp-content-col" key={location.pathname} aria-busy={navPending || undefined}>
      <Suspense fallback={<OmpRouteSkeleton pathname={location.pathname} />}>
        <div className="animate-page-in">
          <Outlet />
        </div>
      </Suspense>
    </section>
  )

  return (
    <OmpActionsProvider>
      <div className="omp-root" data-nav-position={navPosition}>
        <ToastContainer />
        {/* Forwards warning_messages from a 200 soft-failure into the toast queue. */}
        <Alert />
        <div className="omp-header-stack">
          <OmpHeader />
          {isCompact ? (
            <div className="omp-header-mobile">
              {creditsRow}
              {navBlocks(undefined, welcomeNode)}
            </div>
          ) : (
            isTop && navBlocks('top', welcomeNode)
          )}
        </div>
        {announcement &&
          announcement.is_visible !== false &&
          framed(SectionKey.AnnouncementBar, announcement, <OmpAnnouncementBar data={announcement.data} />)}
        {!isPaymentIssueBannerDisabled &&
          page === PORTAL_PAGES.Home &&
          moment === MOMENT_SLUGS[Moment.PaymentIssue] && (
            <div className="omp-banner-wrap">
              <OmpPaymentIssueBanner />
            </div>
          )}

        <main className="omp-main">
          <div className="omp-container">
            {/* Left/right nav puts the welcome row in the nav column; top nav puts the greeting in the bar. */}
            {!isCompact && isTop && creditsRow}

            {isTop || isCompact ? (
              content
            ) : (
              <div className="omp-layout-grid">
                <aside className="omp-nav-col">
                  {welcomeRow}
                  {navBlocks()}
                </aside>
                {content}
              </div>
            )}
          </div>
        </main>

        {showFooter && <OmpFooter />}
        <OmpActionRouter />
        <OmpPaymentUpdateAutoOpen />
        <OmpLifeline />
        <OmpCrossSellCartHost />
        <GlobalLoading />

        {environment.development && !isPreview && (
          <button
            type="button"
            className="omp-dev-refresh"
            onClick={refreshSettings}
            disabled={refreshing}
            title="Refresh portal settings / page styles from the backend">
            <RotateCw size={16} className={refreshing ? 'omp-dev-refresh-spin' : undefined} />
            {refreshing ? 'Refreshing…' : 'Refresh settings'}
          </button>
        )}
      </div>
    </OmpActionsProvider>
  )
}

export default OmpLayout
